import Tweener from '@/common/Tweener';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import animationData from '@/static/life/bouncy-family.json';
import { Box, useLottie } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { lerp } from '@lemonade-hq/ts-helpers';
import type React from 'react';
import { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { LiveLerpTrigger } from './LiveLerpTrigger';
import useFamilyAnimation from './hooks/useFamilyAnimation';

type ResponsiveNumberPair = [number, number];
export type ResponsiveValue =
  | [ResponsiveNumberPair]
  | [ResponsiveNumberPair, ResponsiveNumberPair]
  | [ResponsiveNumberPair, ResponsiveNumberPair, ResponsiveNumberPair];

export const FAMILY_SCROLL_RANGE: ResponsiveValue = [
  [0.0, 0.075],
  [0.0, 0.075],
  [0.0, 0.1],
];

const FAMILY_HUG_SCROLL_THRESHOLD = [0.008, 0.008, 0.01] as [number, number, number];

const familyMobileMarginTop = (): string => '-770px';
const familyMobileTransform = (t: number): string => `translateX(4.5%) translateY(${lerp(380, 10, t)}px) scale(1.2)`;
const familyMobileTween = (t: number): { marginTop: string; transform: string } => {
  return {
    marginTop: familyMobileMarginTop(),
    transform: familyMobileTransform(t),
  };
};

const familyDesktopMarginTop = (): string => '-1100px';
const familyDesktopTransform = (t: number): string => `translateY(${lerp(380, 0, t)}px)`;
const familyDesktopTween = (t: number): { marginTop: string; transform: string } => {
  return {
    marginTop: familyDesktopMarginTop(),
    transform: familyDesktopTransform(t),
  };
};

const Family = styled(Box)`
  pointer-events: none;
  position: relative;
  z-index: 0;

  ${MEDIA_QUERIES.upToDesktop`
        transform: ${familyMobileTransform(0)} ;

        .DOG {
            display: none !important;
        }

    `}

  ${MEDIA_QUERIES.desktop`
        transform: ${familyDesktopTransform(0)};
    `}
`;

const FamilyAnimation: React.FC = () => {
  const { smoothRelativeScroll: t } = useSmoothScroll();
  const familyInBounceMode = useRef(true);
  const familyLottieContainer = useRef<HTMLDivElement>(null);
  const familyLottie = useLottie(familyLottieContainer, {
    animationData: animationData,
    lottieConfig: { autoplay: true, loop: true },
  });
  useFamilyAnimation({
    lottieRef: familyLottie,
    isBouncingRef: familyInBounceMode,
  });

  const toggleBounce = useCallback((value: boolean) => {
    familyInBounceMode.current = !value;
  }, []);

  return (
    <LiveLerpTrigger t={t} trigger={FAMILY_HUG_SCROLL_THRESHOLD} onTrigger={toggleBounce}>
      <Tweener t={t} tt={FAMILY_SCROLL_RANGE} tween={[familyMobileTween, familyMobileTween, familyDesktopTween]}>
        <Family
          ref={familyLottieContainer}
          mb={[40, 40, -50]}
          mt={[familyMobileMarginTop(), familyMobileMarginTop(), familyDesktopMarginTop()]}
          height={[733, 733, 1383]}
          zIndex={0}
          width={['175%', '175%', 1]}
        />
      </Tweener>
    </LiveLerpTrigger>
  );
};

export default FamilyAnimation;
